import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import {
  type AIProviderId,
  filterAIProviderModels,
  getAIProvider,
  isEffortLevel,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { MANAGED_AI_CONNECTOR_ID } from "../../../shared/aiProviders/managedAiConnector";
import { config } from "../../environment";
import { connectorAllowedModels } from "../../../shared/connectors/models";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useConnectorCreator } from "../../hooks/useConnectorCreator";
import { useManagedAIProvider } from "../../hooks/useManagedAIProvider";
import { useHasAccess } from "../../hooks/useHasAccess";
import { getConfigSubtitle } from "../../lib/aiProvider";
import { Button } from "../Button";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { UsageLimitsSection } from "../ConnectorModal/UsageLimitsSection";
import { DangerSection } from "../Settings/DangerSection";
import { HeaderTitleStack } from "../HeaderTitleStack";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { ProviderLogo } from "../ProviderLogo";
import { Text } from "../Text";
import { useToast } from "../Toast";
import { WavyLine } from "../WavyLine";
import { AiProviderAccessCard } from "./AiProviderAccessCard";
import { AiProviderSettingsTab } from "./AiProviderSettingsTab";
import { candidateConnectors } from "./candidateConnectors";
import {
  type AIConfigDraft,
  draftSaveDisabled,
  emptyDraft,
  managedDraft,
  saveErrorMessage,
  toDraft,
  toMutationArgs,
} from "./aiProviderDraft";

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const HeaderLeft = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
`;

const Footer = styled("div")`
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;

const HeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

type Props = {
  id: string | null;
  initialProvider?: AIProviderId;
  onClose: () => void;
};

export function AiProviderModal({ id, initialProvider, onClose }: Props) {
  const z = useZero();
  const { showToast } = useToast();
  const isCreate = id == null;
  const isManaged = id === MANAGED_AI_PROVIDER_ID;
  const [createdId] = useState(() => nanoid(12));
  const providerId = id ?? createdId;

  const [provider, providerResult] = useQuery(
    queries.loadAiProvider({ aiProviderId: providerId })
  );
  const [connectorOptions, connectorOptionsResult] = useQuery(
    queries.connectorsIndex()
  );
  const [activeProvider, activeProviderResult] = useQuery(
    queries.activeAIProvider()
  );
  const {
    managedProvider: envManagedProvider,
    availableProvider,
    disabled: managedDisabled,
    locked: managedLocked,
    managedChatModel,
    managedEffort,
    tenantLoaded,
  } = useManagedAIProvider();
  const canManage = useHasAccess("tenant:ai_providers_manage");

  const [draft, setDraft] = useState<AIConfigDraft | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const seededFor = useRef<string | null>(null);

  const setConnector = (connectedAppId: string | null) => {
    setDraft((current) => (current ? { ...current, connectedAppId } : current));
    setIsDirty(true);
  };
  const createConnector = useConnectorCreator(setConnector);

  useEffect(() => {
    if (seededFor.current === providerId) return;
    if (isManaged) {
      if (!envManagedProvider || !tenantLoaded) return;
      seededFor.current = providerId;
      // oxlint-disable-next-line react/react-compiler -- EffectSetState: seeds the draft once the managed provider and tenant config finish loading asynchronously
      setDraft(
        managedDraft(envManagedProvider, {
          chatModel: managedChatModel,
          effort: isEffortLevel(managedEffort) ? managedEffort : null,
        })
      );
    } else if (isCreate) {
      seededFor.current = providerId;
      setDraft(emptyDraft(initialProvider));
    } else if (provider) {
      seededFor.current = providerId;
      setDraft(toDraft(provider, provider.appUsage?.connectedAppId ?? null));
    } else {
      return;
    }
    setIsDirty(false);
  }, [
    provider,
    isCreate,
    isManaged,
    envManagedProvider,
    tenantLoaded,
    managedChatModel,
    managedEffort,
    providerId,
    initialProvider,
  ]);

  const loading =
    (!isCreate && !isManaged && providerResult?.type !== "complete") ||
    ((isCreate || isManaged) && activeProviderResult?.type !== "complete") ||
    (isManaged && !tenantLoaded) ||
    connectorOptionsResult?.type !== "complete";

  if (loading) {
    return (
      <Modal
        open
        onClose={onClose}
        style={{
          width: 680,
          height: 760,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <WavyLine />
      </Modal>
    );
  }

  if (!draft || (!isCreate && !isManaged && !provider)) return null;

  const providerType = draft.provider;
  const label = getAIProvider(providerType).label;
  const reusePrior =
    !isCreate &&
    provider != null &&
    resolveAIProviderId(provider) === draft.provider;
  const saveDisabled = isManaged ? false : draftSaveDisabled(draft, reusePrior);
  const nextActive = isCreate
    ? activeProvider == null && availableProvider == null
    : (provider?.active ?? false);

  const draftProvider = getAIProvider(draft.provider);
  const connectorChoices = candidateConnectors(draft, connectorOptions);
  const persistedConnector = provider?.appUsage?.connectedApp ?? null;
  const selectedConnector = draft.connectedAppId
    ? (connectorOptions.find(
        (connector) => connector.id === draft.connectedAppId
      ) ??
      (persistedConnector?.id === draft.connectedAppId
        ? persistedConnector
        : null))
    : null;
  const connectorModels = selectedConnector
    ? connectorAllowedModels(selectedConnector.configuration)
    : [];
  const allowedModels = filterAIProviderModels(draft.provider, connectorModels);
  // Spend limits live in the connector ledger keyed by the provider's backing
  // connector (the managed sentinel for managed AI); without one there's no
  // metered usage to cap.
  const spendConnectorId = isManaged
    ? MANAGED_AI_CONNECTOR_ID
    : draft.connectedAppId;

  const save = async () => {
    if (saveDisabled || !canManage) return;
    try {
      if (isManaged) {
        z.mutate(
          sharedMutators.setManagedAIProviderConfig({
            chatModel: draft.chatModel.trim() || null,
            effort: draft.effort,
          })
        );
      } else {
        z.mutate(
          sharedMutators.updateAIConfig(
            toMutationArgs(providerId, nextActive, draft)
          )
        );
      }
      setIsDirty(false);
    } catch (error) {
      showToast({
        icon: "circle-off",
        message: saveErrorMessage(error),
        buttonLabel: "Dismiss",
        variant: "error",
      });
    }
  };

  const finish = async () => {
    if (isDirty && canManage && !saveDisabled) {
      await save();
    }
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      style={{
        width: 680,
        height: 760,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header>
        <HeaderLeft>
          <ConnectorLogoContainer
            size={32}
            borderRadius={8}
            hoverBackground={false}
          >
            {isManaged ? (
              <Icon name="tines-mark" size={16} />
            ) : (
              <ProviderLogo provider={providerType} size={16} />
            )}
          </ConnectorLogoContainer>
          <HeaderTitleStack>
            <Text
              variant="text-regular-md"
              color="var(--text-primary)"
              ellipsis
            >
              {isManaged ? "Managed by 3B" : isCreate ? `Add ${label}` : label}
            </Text>
            {isManaged ? (
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                Anthropic and OpenAI
              </Text>
            ) : isCreate ? (
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {draftProvider.description}
              </Text>
            ) : (
              provider && (
                <Text
                  variant="text-small"
                  color="var(--text-secondary)"
                  ellipsis
                >
                  {getConfigSubtitle(
                    provider,
                    selectedConnector?.configuration
                  )}
                </Text>
              )
            )}
          </HeaderTitleStack>
        </HeaderLeft>
        <HeaderRight>
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </HeaderRight>
      </Header>
      <Body style={canManage ? undefined : { borderBottom: 0 }}>
        <AiProviderSettingsTab
          draft={draft}
          reusePrior={reusePrior}
          disabled={!canManage}
          onChange={(next) => {
            setDraft(next);
            setIsDirty(true);
          }}
          connectors={connectorChoices}
          selectedConnectorName={selectedConnector?.name ?? null}
          allowedModels={allowedModels}
          connectorModelsRestricted={connectorModels.length > 0}
          managedModelOptions={isManaged ? [...config.managedChatModels] : null}
          onConnectorChange={setConnector}
          onAddConnector={() => {
            if (draftProvider.connectorIdentifier)
              void createConnector(draftProvider.connectorIdentifier);
          }}
          onProviderChange={
            isCreate
              ? (next) => {
                  setDraft(emptyDraft(next));
                  setIsDirty(true);
                }
              : null
          }
          managed={isManaged}
        />
        {!isManaged && (
          <div style={{ marginTop: 16 }}>
            <AiProviderAccessCard
              inheritConnectorAccess={draft.inheritConnectorAccess}
              connectedAppId={draft.connectedAppId}
              connectorName={selectedConnector?.name ?? null}
              isDefault={nextActive}
              disabled={!canManage}
              onChange={(inheritConnectorAccess) => {
                setDraft({ ...draft, inheritConnectorAccess });
                setIsDirty(true);
              }}
            />
          </div>
        )}
        {spendConnectorId && (
          <div style={{ marginTop: 16 }}>
            <UsageLimitsSection connectorId={spendConnectorId} />
          </div>
        )}
        {canManage && isManaged && (
          <DangerSection
            style={{ marginTop: 16 }}
            tone="neutral"
            title={managedDisabled ? "Enable managed AI" : "Disable managed AI"}
            description={
              managedDisabled
                ? "Turn 3B-managed models back on for this tenant."
                : "Turn off 3B-managed models for this tenant. Usage stops but history is kept."
            }
            actionLabel={managedDisabled ? "Enable" : "Disable"}
            disabled={
              managedLocked || (!managedDisabled && activeProvider == null)
            }
            disabledReason={
              managedLocked
                ? "Not available for this tenant"
                : !managedDisabled && activeProvider == null
                  ? "Set another provider as the default first"
                  : undefined
            }
            onAction={() => {
              z.mutate(
                sharedMutators.setManagedAIProviderDisabled({
                  disabled: !managedDisabled,
                })
              );
            }}
          />
        )}
        {canManage && !isCreate && !isManaged && (
          <DangerSection
            style={{ marginTop: 16 }}
            title="Delete AI provider"
            description="Removes this provider. This can’t be undone."
            actionLabel="Delete provider"
            confirm={{
              confirmLabel: "Delete",
              description: `“${label}” will be removed. This can’t be undone.`,
              onConfirm: () => {
                z.mutate(sharedMutators.deleteAIConfig({ id: providerId }));
                onClose();
              },
            }}
          />
        )}
      </Body>
      {canManage && (
        <Footer>
          <Button
            variant="primary"
            size="l"
            onClick={finish}
            disabled={saveDisabled}
          >
            Done
          </Button>
        </Footer>
      )}
    </Modal>
  );
}
